import { marked } from 'marked';
import {
  SITE_URL,
  LOCALES,
  DEFAULT_LOCALE,
  localePath,
  getStrings,
  type Lang,
} from './i18n';
import { STORE_IOS, STORE_ANDROID } from './constants';
import { storeBadgesHtml } from './storeBadges';
import { iphoneFrameHtml } from './iphoneFrame';

export { SITE_URL };
export type BlogLang = Lang;

export interface FaqItem {
  q: string;
  a: string;
}

export interface Post {
  lang: Lang;
  key: string;
  slug: string;
  title: string;
  description: string;
  date: string;
  updated: string;
  keywords: string[];
  cover: string;
  category: string; // topic id from blogTopics.ts
  // 'contain' shows the whole cover (e.g. tall app screenshots) instead of cropping it.
  coverFit: 'cover' | 'contain';
  readingTime: number;
  html: string;
  faq: FaqItem[];
}

export const blogBase = (lang: Lang) => localePath(lang, '/blog');

// Blog chrome strings (home, blog, cta, minRead, …) sourced from the JSON locales.
export const strings = Object.fromEntries(
  LOCALES.map((l) => [l, getStrings(l).blogChrome]),
) as Record<Lang, ReturnType<typeof getStrings>['blogChrome']>;

const raw = import.meta.glob('../content/blog/*/*.md', {
  query: '?raw',
  import: 'default',
  eager: true,
}) as Record<string, string>;

function parseFrontmatter(src: string): { data: Record<string, string>; body: string } {
  const match = src.match(/^---\r?\n([\s\S]*?)\r?\n---\r?\n?([\s\S]*)$/);
  if (!match) return { data: {}, body: src };
  const data: Record<string, string> = {};
  for (const line of match[1].split(/\r?\n/)) {
    const idx = line.indexOf(':');
    if (idx === -1) continue;
    const key = line.slice(0, idx).trim();
    let val = line.slice(idx + 1).trim();
    if (
      (val.startsWith('"') && val.endsWith('"')) ||
      (val.startsWith("'") && val.endsWith("'"))
    ) {
      val = val.slice(1, -1);
    }
    data[key] = val;
  }
  return { data, body: match[2] };
}

function extractFaq(body: string): { body: string; faq: FaqItem[] } {
  const match = body.match(/<!--\s*faq\s*([\s\S]*?)-->/i);
  if (!match) return { body, faq: [] };
  let faq: FaqItem[] = [];
  try {
    faq = JSON.parse(match[1].trim());
  } catch {
    faq = [];
  }
  return { body: body.replace(match[0], '').trim(), faq };
}

// Same badge markup as StoreBadges.tsx (React component) — duplicated here as
// raw HTML since this is injected into markdown-rendered article content,
// outside the React tree.

function ctaHtml(lang: Lang): string {
  return `<div class="not-prose ap-midcta my-10 p-6 text-center">
  <p class="font-heading text-ink text-xl font-black mb-4">${strings[lang].midCta}</p>
  <div class="flex flex-wrap justify-center gap-3">
    ${storeBadgesHtml(lang, STORE_IOS, STORE_ANDROID)}
  </div>
</div>`;
}

function enhanceImages(html: string): string {
  html = html.replace(/<img /g, '<img loading="lazy" decoding="async" ');
  return html.replace(/<p>(<img[^>]*>)<\/p>/g, (_m, img) => {
    const alt = img.match(/alt="([^"]*)"/);
    const caption = alt && alt[1] ? `<figcaption>${alt[1]}</figcaption>` : '';
    // App screenshots are marked with #phone in the src — render them inside
    // the shared iPhone frame (iphone-frame.css). Everything else stays a plain figure.
    if (/src="[^"]*#phone"/.test(img)) {
      const framed = img.replace(/(src="[^"]*?)#phone(")/, '$1$2');
      return `<figure class="gm-figure">${iphoneFrameHtml(framed, 'phone-mock')}${caption}</figure>`;
    }
    return `<figure class="gm-figure">${img}${caption}</figure>`;
  });
}

function render(body: string, lang: Lang): string {
  const withCta = body.replace(/\{\{\s*cta\s*\}\}/g, ctaHtml(lang));
  return enhanceImages(marked.parse(withCta, { async: false }) as string);
}

function build(path: string, src: string): Post {
  const parts = path.split('/');
  const file = parts.pop()!;
  const langSeg = parts.pop()!;
  const lang = (LOCALES as readonly string[]).includes(langSeg)
    ? (langSeg as Lang)
    : DEFAULT_LOCALE;
  const fileSlug = file.replace(/\.md$/, '');
  const { data, body } = parseFrontmatter(src);
  const { body: clean, faq } = extractFaq(body);
  const words = clean.split(/\s+/).filter(Boolean).length;
  return {
    lang,
    key: fileSlug,
    slug: data.slug || fileSlug,
    title: data.title || fileSlug,
    description: data.description || '',
    date: data.date || '',
    updated: data.updated || data.date || '',
    keywords: (data.keywords || '').split(',').map((s) => s.trim()).filter(Boolean),
    cover: data.cover || '/og-image.png',
    category: data.category || '',
    coverFit: data.coverFit === 'contain' ? 'contain' : 'cover',
    readingTime: Math.max(1, Math.round(words / 200)),
    html: render(clean, lang),
    faq,
  };
}

const builtPosts: Post[] = Object.entries(raw).map(([path, src]) => build(path, src));

// Topic filter: translations inherit the topic (`category`) of the EN article with the same key.
const enCategory = new Map(builtPosts.filter((p) => p.lang === DEFAULT_LOCALE).map((p) => [p.key, p.category]));
for (const p of builtPosts) if (!p.category) p.category = enCategory.get(p.key) || '';

// Scheduled posts (dated after the build) are left out entirely; the daily redeploy publishes them.
const allPosts: Post[] = builtPosts
  .filter((p) => p.date <= __BUILD_DATE__)
  .sort((a, b) => (a.date < b.date ? 1 : -1));

// Normalize inline cross-article links to the correct locale + that language's slug.
const slugToKey: Record<string, string> = {};
for (const p of allPosts) slugToKey[p.slug] = p.key;
const scheduledSlugs = new Set(
  builtPosts.filter((p) => !(p.slug in slugToKey)).map((p) => p.slug),
);
function fixCrossLinks(html: string, lang: Lang): string {
  // Links to not-yet-published posts render as plain text instead of 404ing.
  html = html.replace(
    /<a href="[^"]*\/blog\/([^"#?/]+)\/?[^"]*"[^>]*>([\s\S]*?)<\/a>/g,
    (whole, slug: string, text: string) => (scheduledSlugs.has(slug) ? text : whole),
  );
  return html.replace(/href="([^"]*\/blog\/[^"#?]+)([^"]*)"/g, (whole, urlPath: string, tail: string) => {
    const slug = urlPath.split('/blog/')[1].replace(/\/$/, '');
    const key = slugToKey[slug];
    if (!key) return whole;
    const target =
      allPosts.find((p) => p.key === key && p.lang === lang) ||
      allPosts.find((p) => p.key === key && p.lang === DEFAULT_LOCALE);
    if (!target) return whole;
    return `href="${localePath(lang, `/blog/${target.slug}`)}${tail}"`;
  });
}
for (const p of allPosts) p.html = fixCrossLinks(p.html, p.lang);

export const getPosts = (lang: Lang): Post[] => allPosts.filter((p) => p.lang === lang);
export const getPost = (lang: Lang, slug: string): Post | undefined =>
  allPosts.find((p) => p.lang === lang && p.slug === slug);

// All language versions of a post (paired by shared filename key).
export const alternatesFor = (post: Post): Post[] =>
  allPosts.filter((p) => p.key === post.key);
// First translation in another language (back-compat helper).
export const alternateFor = (post: Post): Post | undefined =>
  allPosts.find((p) => p.key === post.key && p.lang !== post.lang);

export function postAltHref(currentLang: Lang, currentSlug: string, target: Lang): string {
  const current = getPost(currentLang, currentSlug);
  const alt = current && allPosts.find((p) => p.key === current.key && p.lang === target);
  return alt ? localePath(target, `/blog/${alt.slug}`) : localePath(target, '/blog');
}
